import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { arClass } from "../../../../utils/useArClass";
import Txt from "../../../Text";
import CustomSelect from "../../../CustomSelect";
import api from "../../../../api";
import { showAlert } from "../../../../redux/alert/alertActions";
import { getPermissionsDetails } from "../../../../utils/permissionsDetails";
import {
  ArrowLeft,
  Save,
  Loader,
  Users,
  CheckCircle,
  Briefcase,
  User,
  Flag,
  NotebookTabs,
  Lock,
  ShieldCheck,
} from "lucide-react";
import textTranslator from "../../../../utils/useTextTranslator";
import { useUser } from "../../../../authentication/authentication";
import RoleListPreview from "../../../RoleListPreview";
import RoleBadge from "../../../RoleBadge";

const Text = ({ children, className, page = "MY_REQUESTS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

const priorityTypes = {
  LOW: {
    label: {
      en: "Low",
      ar: "منخفض",
      fr: "Faible",
    },
    value: "LOW",
    color: "green",
  },
  MEDIUM: {
    label: {
      en: "Medium",
      ar: "متوسط",
      fr: "Moyen",
    },
    value: "MEDIUM",
    color: "yellow",
  },
  HIGH: {
    label: {
      en: "High",
      ar: "مرتفع",
      fr: "Élevé",
    },
    value: "HIGH",
    color: "red",
  },
  CRITICAL: {
    label: {
      en: "Critical",
      ar: "حرج",
      fr: "Critique",
    },
    value: "CRITICAL",
    color: "gray",
  },
};

const roleChangeTypes = {
  PROMOTION: {
    label: {
      en: "Promotion",
      ar: "ترقية",
      fr: "Promotion",
    },
    value: "PROMOTION",
  },
  DEMOTION: {
    label: {
      en: "Demotion",
      ar: "تخفيض",
      fr: "Rétrogradation",
    },
    value: "DEMOTION",
  },
  LATERAL_MOVE: {
    label: {
      en: "Lateral Move",
      ar: "نقل جانبي",
      fr: "Mouvement Latéral",
    },
    value: "LATERAL_MOVE",
  },
  ROLE_CHANGE: {
    label: {
      en: "Role Change",
      ar: "تغيير الدور",
      fr: "Changement de Rôle",
    },
    value: "ROLE_CHANGE",
  },
};

const rolesOptions = {
  owner: {
    label: {
      en: "Owner",
      ar: "مالك",
      fr: "Propriétaire",
    },
    value: "owner",
  },
  admin: {
    label: {
      en: "Admin",
      ar: "مسؤول",
      fr: "Admin",
    },
    value: "admin",
  },
  subadmin: {
    label: {
      en: "Sub Admin",
      ar: "مسؤول فرعي",
      fr: "Sous-Admin",
    },
    value: "subadmin",
  },
  doctor: {
    label: {
      en: "Doctor",
      ar: "طبيب",
      fr: "Docteur",
    },
    value: "doctor",
  },
  nurse: {
    label: {
      en: "Nurse",
      ar: "ممرضة",
      fr: "Infirmière",
    },
    value: "nurse",
  },
  receptionist: {
    label: {
      en: "Receptionist",
      ar: "موظف استقبال",
      fr: "Réceptionniste",
    },
    value: "receptionist",
  },
  pharmacist: {
    label: {
      en: "Pharmacist",
      ar: "صيدلي",
      fr: "Pharmacien",
    },
    value: "pharmacist",
  },
  accountant: {
    label: {
      en: "Accountant",
      ar: "محاسب",
      fr: "Comptable",
    },
    value: "accountant",
  },
  lab_technician: {
    label: {
      en: "Lab Technician",
      ar: "فني مختبر",
      fr: "Technicien de Laboratoire",
    },
    value: "lab_technician",
  },
  radiologist: {
    label: {
      en: "Radiologist",
      ar: "أخصائي أشعة",
      fr: "Radiologue",
    },
    value: "radiologist",
  },
  staff: {
    label: {
      en: "Staff",
      ar: "موظف",
      fr: "Personnel",
    },
    value: "staff",
  },
};

const CreateRoleRequest = ({ onCancel }) => {
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetails, setMemberDetails] = useState(null);
  const [loadingMember, setLoadingMember] = useState(false);
  const localUser = useUser();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "MEDIUM",
    roleChangeType: "PROMOTION",
    currentRoles: [],
    requestedRoles: [],
    justification: "",
  });

  const [selectedRole, setSelectedRole] = useState("");
  const [roleDetails, setRoleDetails] = useState(null);
 
  // STEP 1: Fetch team members on mount
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        setFetching(true);
        const queryParams = new URLSearchParams({
          page: 1,
          limit: 50,
        });
        const response = await api.get(`users/team/?${queryParams.toString()}`);
        setTeamMembers(response.data?.results || []);
      } catch (error) {
        
        dispatch(
          showAlert({
            type: "error",
            title: "Error",
            message: "Failed to load team members",
          }),
        );
      } finally {
        setFetching(false);
      }
    };
    fetchTeamMembers();
  }, [dispatch]);

  // STEP 2: Fetch selected member details
  useEffect(() => {
    if (!selectedMember) return;

    const fetchMemberDetails = async () => {
      try {
        setLoadingMember(true);
        const response = await api.get(
          `users/team/member/${selectedMember.id}`,
        );
        const member = response.data?.data;
        setMemberDetails(member);

        // Pre-fill current role
        if (member?.role) {
          setFormData((prev) => ({
            ...prev,
            currentRoles: member.roles || [],
          }));
        }
      } catch (error) {
      
        dispatch(
          showAlert({
            type: "error",
            title: "Error",
            message: "Failed to load member details",
          }),
        );
      } finally {
        setLoadingMember(false);
      }
    };
    fetchMemberDetails();
  }, [selectedMember, dispatch]);

  // STEP 3: Fetch role details when role is selected
  useEffect(() => {
    if (!selectedRole) {
      setRoleDetails(null);
      return;
    }

    const details = getPermissionsDetails(selectedRole);
    setRoleDetails(details);
  }, [selectedRole, defaultLanguage]);

  const handleInputChange = (e) => {
  
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedMember) {
      dispatch(
        showAlert({
          type: "error",
          title: "Error",
          message: "Please select a team member",
        }),
      );
      return;
    }

    setLoading(true);
    
    try {
      const response = await api.post("it-requests", {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        requestType: "ROLE_REQUEST",
        targetUserId: selectedMember.id,
        assignedToId: selectedMember.id,
        roleDetails: {
          roleChangeType: formData.roleChangeType,
          requestedRoles: formData.requestedRoles,
          justification: formData.justification,
        },
      });

      dispatch(
        showAlert({
          type: "success",
          title: textTranslator("Success", "MY_REQUESTS"),
          message: textTranslator("Role request created successfully", "MY_REQUESTS"),
        }),
      );

      navigate("/my-requests");
    } catch (error) {
   
      dispatch(
        showAlert({
          type: "error",
          title: "Error",
          message: error?.response?.data?.message || "Failed to create request",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={arClass(
        `min-h-screen ${
          darkMode
            ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
            : "bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50"
        } transition-colors duration-300 py-8`,
      )}
    >
      <div className={arClass(" mx-auto px-4 sm:px-6 lg:px-8")}>
        {/* Back Button */}
        <motion.button
          onClick={onCancel}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className={arClass(
            `flex items-center gap-2 px-4 py-2 rounded-lg font-medium mb-8 transition-all ${
              darkMode
                ? "bg-gray-800/50 hover:bg-gray-700/80 text-gray-300 hover:text-white border border-gray-700"
                : "bg-white/50 hover:bg-white text-gray-600 hover:text-gray-900 border border-gray-200 shadow-sm"
            }`,
          )}
          whileHover={{ scale: 1.05, x: -5 }}
          whileTap={{ scale: 0.95 }}
        >
          <ArrowLeft className="w-4 h-4" />
          <Text>Back</Text>
        </motion.button>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={arClass(
            `rounded-2xl overflow-hidden shadow-2xl border ${
              darkMode
                ? "bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700/50"
                : "bg-white border-gray-200/50"
            }`,
          )}
        >
          {/* Header Section */}
          <div
            className={arClass(
              `bg-gradient-to-r from-purple-600 to-pink-600 px-2 py-4 sm:p-6 relative overflow-hidden`,
            )}
          >
            <motion.div
              className="absolute inset-0 opacity-10"
              animate={{
                background: [
                  "radial-gradient(circle at 20% 50%, #ffffff 0%, transparent 50%)",
                  "radial-gradient(circle at 80% 50%, #ffffff 0%, transparent 50%)",
                  "radial-gradient(circle at 20% 50%, #ffffff 0%, transparent 50%)",
                ],
              }}
              transition={{ duration: 4, repeat: Infinity }}
            />
            <div className={arClass("relative z-10")}>
              <div className={arClass("flex items-center gap-3 mb-2")}>
                <Briefcase className="text-white w-10 h-10 max-sm:w-6 max-sm:h-6" />
                <h1 className="text-3xl max-sm:text-xl font-bold text-white">
                  <Text>Create Role Request</Text>
                </h1>
              </div>
              <p className={arClass("text-pink-100 text-sm")}>
                <Text>Request role changes for your team members</Text>
              </p>
            </div>
          </div>

          {/* Content */}
          <div className={arClass(`px-2 py-4 sm:p-6`)}>
            {fetching ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={arClass(
                  "flex flex-col items-center justify-center py-16",
                )}
              >
                <Loader
                  className={`text-5xl animate-spin mb-4 w-12 h-12 ${darkMode ? "text-purple-400" : "text-purple-600"}`}
                />
                <p
                  className={arClass(
                    `${darkMode ? "text-gray-400" : "text-gray-600"}`,
                  )}
                >
                  <Text>Loading team members</Text>...
                </p>
              </motion.div>
            ) : (
              <>
                {/* Team Members Banner */}
                {teamMembers.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className={arClass(
                      `mb-8 px-2 py-4 sm:p-6 rounded-xl border-2 ${
                        darkMode
                          ? "bg-gradient-to-r from-blue-900/30 to-cyan-900/30 border-blue-600/50"
                          : "bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200"
                      }`,
                    )}
                  >
                    <div className={arClass("flex items-center gap-3")}>
                      <Users
                        className={arClass(
                          "text-2xl flex-shrink-0 " +
                            (darkMode ? "text-blue-400" : "text-blue-600"),
                        )}
                      />
                      <div>
                        <p
                          className={arClass(
                            `text-sm font-medium ${darkMode ? "text-blue-200" : "text-blue-700"}`,
                          )}
                        >
                          <Text>Step 1: Select Team Member</Text>
                        </p>
                        <p
                          className={arClass(
                            `text-sm ${darkMode ? "text-blue-100/70" : "text-blue-900/70"}`,
                          )}
                        >
                          <Text>
                            Choose a team member to request role changes for
                          </Text>
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Form */}
                <form
                  onSubmit={handleSubmit}
                  className={arClass("normal-form space-y-6")}
                >
                  {/* Section 1: Select Team Member */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className={arClass(
                      `p-6 rounded-xl border-2 ${
                        darkMode
                          ? "bg-gray-800/50 border-gray-700/50"
                          : "bg-gray-50/50 border-gray-200"
                      }`,
                    )}
                  >
                    <div className={arClass("flex items-center gap-2 mb-6")}>
                      <Users
                        className={arClass(
                          "text-lg " +
                            (darkMode ? "text-blue-400" : "text-blue-600"),
                        )}
                      />
                      <h2
                        className={arClass(
                          `text-lg max-sm:text-sm font-bold ${darkMode ? "text-white" : "text-gray-900"}`,
                        )}
                      >
                        <Text>Team Members</Text>
                      </h2>
                    </div>

                    <div
                      className={arClass(
                        "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4",
                      )}
                    >
                      {teamMembers.map((member) =>
                       
                          <motion.button
                            key={member.id}
                            type="button"
                            onClick={() => setSelectedMember(member)}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={arClass(
                              `p-4 rounded-lg border transition-all flex flex-col items-start relative ${
                                selectedMember?.id === member.id
                                  ? darkMode
                                    ? "border-blue-500 bg-blue-900/20"
                                    : "border-blue-500 bg-blue-50"
                                  : darkMode
                                    ? "border-gray-600 hover:border-gray-500"
                                    : "border-gray-200 hover:border-gray-300"
                              }`,
                            )}
                          >
                            {String(localUser.id) === String(member.id) && (
                            <div
                              className={arClass(
                                "absolute top-2 right-2 p-1.5 bg-green-500/20 border border-green-500 z-[1000] font-medium rounded-md flex items-center gap-1 duration-200 transition-all",
                              )}
                            >
                              <ShieldCheck className="w-3 h-3 text-green-500" />
                              <span className={arClass("text-green-600 text-xs font-semibold")}>
                                <Text>This is you</Text>
                              </span>
                            </div>
                          )}
                            <div
                              className={arClass(
                                "flex items-center gap-3 mb-2",
                              )}
                            >
                              
                              <img
                                src={member.profileImage}
                                alt={member.firstName}
                                className={arClass(
                                  "w-10 h-10 rounded-full object-cover",
                                )}
                              />
                              <div
                                className={arClass(
                                  "flex-1 flex flex-col items-start min-w-0",
                                )}
                              >
                                <p
                                  className={arClass(
                                    `font-semibold text-sm truncate ${darkMode ? "text-white" : "text-gray-900"}`,
                                  )}
                                >
                                  {member.firstName} {member.lastName}
                                </p>
                                <RoleListPreview
                  paddingY="py-1"
                  my="my-0"
                  style="bg-text"  
                  roles={(member.roles || []).map(
                                    (role) => role?.name || role,
                                  )} />
                              </div>
                            </div>
                            {selectedMember?.id === member.id && (
                              <div
                                className={arClass(
                                  "flex items-center gap-1 text-blue-600 text-xs mt-2",
                                )}
                              >
                                <CheckCircle className={arClass("w-4 h-4")} />
                                <span>
                                  <Text>Selected</Text>
                                </span>
                              </div>
                            )}
                          </motion.button>
                     
                      )}
                    </div>
                  </motion.div>

                  {/* Section 2: Request Details */}
                  {selectedMember && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.25 }}
                      className={arClass(
                        `p-6 rounded-xl border-2 ${
                          darkMode
                            ? "bg-gray-800/50 border-gray-700/50"
                            : "bg-gray-50/50 border-gray-200"
                        }`,
                      )}
                    >
                      <div className={arClass("flex items-center gap-2 mb-6")}>
                        <User
                          className={arClass(
                            "text-lg " +
                              (darkMode ? "text-cyan-400" : "text-cyan-600"),
                          )}
                        />
                        <h2
                          className={arClass(
                            `text-lg max-sm:text-sm font-bold ${darkMode ? "text-white" : "text-gray-900"}`,
                          )}
                        >
                          <Text>Request Information</Text>
                        </h2>
                      </div>

                      <div className={arClass("space-y-4")}>
                        {/* Title */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.25 }}
                        >
                          <label
                            className={arClass(
                              `text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`,
                            )}
                          >
                            <Text>Title</Text>{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            placeholder={textTranslator(
                              "titlePlaceholder",
                              "MY_REQUESTS",
                            )}
                            required
                            className={arClass(
                              `w-full mt-4 px-4 py-3 rounded-lg border transition-all ${
                                darkMode
                                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                              }`,
                            )}
                          />
                        </motion.div>

                        {/* Description */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 }}
                        >
                          <label
                            className={arClass(
                              `text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`,
                            )}
                          >
                            <Text>Description</Text>{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder={textTranslator(
                              "descriptionPlaceholder",
                              "MY_REQUESTS",
                            )}
                            required
                            rows={4}
                            className={arClass(
                              `w-full mt-4 px-4 py-3 rounded-lg border transition-all resize-none ${
                                darkMode
                                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                              }`,
                            )}
                          />
                        </motion.div>

                        {/* Priority & Role Change Type */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                          >
                            <label
                              className={arClass(
                                `text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`,
                              )}
                            >
                              <Text>Priority</Text>
                            </label>
                            <CustomSelect
                              options={Object.entries(priorityTypes).map(
                                ([key, value]) => ({
                                  value: key,
                                  label: value.label[defaultLanguage] || key,
                                }),
                              )}
                              value={formData.priority || ""}
                              name="priority"
                              onChange={handleInputChange}
                              placeholder={textTranslator(
                                "priorityPlaceholder",
                                "MY_REQUESTS",
                              )}
                              allowCreate={false}
                              allowSearch={false}
                              className="max-sm:text-sm mt-4"
                            />
                          </motion.div>

                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.35 }}
                          >
                            <label
                              className={arClass(
                                `text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`,
                              )}
                            >
                              <Text>Change Type</Text>
                            </label>
                            <CustomSelect
                              options={Object.entries(roleChangeTypes).map(
                                ([key, value]) => ({
                                  value: key,
                                  label: value.label[defaultLanguage] || key,
                                }),
                              )}
                              value={formData.roleChangeType || ""}
                              name="roleChangeType"
                              onChange={handleInputChange}
                              placeholder={textTranslator(
                                "changeTypePlaceholder",
                                "MY_REQUESTS",
                              )}
                              allowCreate={false}
                              allowSearch={false}
                              className="max-sm:text-sm mt-4"
                            />
                          </motion.div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Section 3: Role Details */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className={arClass(
                      `p-6 rounded-xl border-2 ${
                        darkMode
                          ? "bg-gradient-to-r from-purple-900/20 to-pink-900/20 border-purple-600/50"
                          : "bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200"
                      }`,
                    )}
                  >
                    <div className={arClass("flex items-center gap-2 mb-6")}>
                      <Flag
                        className={arClass(
                          "text-lg " +
                            (darkMode ? "text-purple-400" : "text-purple-600"),
                        )}
                      />
                      <h2
                        className={arClass(
                          `text-lg max-sm:text-sm font-bold ${darkMode ? "text-white" : "text-gray-900"}`,
                        )}
                      >
                        <Text>Role Details</Text>
                      </h2>
                    </div>
                    <div className={arClass("space-y-4")}>
                      {/* Current Roles */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.45 }}
                      >
                        <label
                          className={arClass(
                            `text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`,
                          )}
                        >
                          <Text>Current Roles</Text>
                        </label>
                        <div className={arClass("flex flex-wrap gap-2 mt-2")}>
                          {
                            formData.currentRoles && formData.currentRoles.length > 0 ? (
                              formData.currentRoles.map((role,idx) => (
                                <RoleBadge paddingY="py-1"
                  my="my-0"
                  style="bg-text" key={idx} role={role.name} />
                              ))
                            ) : (
                              <p
                                className={arClass(
                                  `text-sm italic ${darkMode ? "text-gray-500" : "text-gray-400"}`,
                                )}
                              >
                                <Text>No current role assigned</Text>
                              </p>
                            )
                          }
                        </div>
                      </motion.div>

                      {/* Requested Role */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 }}
                      >
                        <label
                          className={arClass(
                            `text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`,
                          )}
                        >
                          <Text>Requested Roles</Text>{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <CustomSelect
                          name="requestedRoles"
                          value={formData.requestedRoles}
                          onChange={(e) => {
                            setSelectedRole(e.target.value);
                            setFormData((prev) => ({
                              ...prev,
                              requestedRoles: e.target.value || [],
                            }));
                          }}
                          options={Object.entries(rolesOptions).map(
                            ([key, value]) => ({
                              value: value.value,
                              label: value.label[defaultLanguage] || key,
                            }),
                          )}
                          multipleSelection={true}
                          placeholder={textTranslator(
                            "selectRolePlaceholder",
                            "MY_REQUESTS",
                          )}
                          allowCreate={false}
                          allowSearch={false}
                          className="max-sm:text-sm mt-4"
                        />
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Submit Button */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className={arClass("flex gap-4 pt-4")}
                  >
                    <motion.button
                      type="submit"
                      disabled={loading}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={arClass(
                        `flex-1 flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-bold transition-all ${
                          loading
                            ? "opacity-50 cursor-not-allowed"
                            : darkMode
                              ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg shadow-purple-600/50"
                              : "bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white shadow-lg shadow-purple-500/50"
                        }`,
                      )}
                    >
                      <Save className="w-5 h-5" />
                      <Text>
                        {loading ? "Creating Request..." : "Create Request"}
                      </Text>
                    </motion.button>
                  </motion.div>
                </form>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CreateRoleRequest;
